import React, { useEffect, useState } from 'react';
import { createContact } from '../../services/ContactApi';
import { getGroups, ApiGroup } from '../../services/GroupApi';

function getCurrentUserId(): string | null {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.id || null;
  } catch (_) {
    return null;
  }
}

export const NewContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    group: '',
    customGroup: '',
    notes: ''
  });
  
  const [showCustomGroup, setShowCustomGroup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [groups, setGroups] = useState<ApiGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    async function loadGroups() {
      setGroupsLoading(true);
      setGroupsError(null);
      try {
        const userId = getCurrentUserId();
        const list = await getGroups(userId ? { userId } : undefined);
        if (!ignore) setGroups(list);
      } catch (e: any) {
        if (!ignore) setGroupsError(e?.message || 'Failed to load groups');
      } finally {
        if (!ignore) setGroupsLoading(false);
      }
    }
    loadGroups();
    return () => { ignore = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        group: (formData.group === 'Other' ? formData.customGroup : formData.group) || undefined,
        notes: formData.notes.trim() || undefined
      };
      await createContact(payload);
      setSuccess('Contact created successfully');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        group: '',
        customGroup: '',
        notes: ''
      });
      setShowCustomGroup(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to create contact');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === 'Other') {
      setShowCustomGroup(true);
      setFormData({ ...formData, group: 'Other' });
    } else {
      setShowCustomGroup(false);
      setFormData({ ...formData, group: selectedValue, customGroup: '' });
    }
  };
  
  const handleCustomGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, customGroup: e.target.value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <h1 className="text-xl font-bold">Create new contact</h1>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow mb-8">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
          )}
          {success && (
            <div className="p-3 rounded bg-green-50 text-green-700 border border-green-200 text-sm">{success}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group
            </label>
            <select
              value={formData.group}
              onChange={handleGroupChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            >
              <option value="">No group</option>
              {groupsLoading && <option value="" disabled>Loading groups...</option>}
              {groupsError && <option value="" disabled>Error loading groups</option>}
              {!groupsLoading && !groupsError && groups.map((g) => (
                <option key={g.id} value={g.name}>{g.name}</option>
              ))}
              <option value="Other">Other</option>
            </select>
            
            {showCustomGroup && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Group Name
                </label>
                <input
                  type="text"
                  value={formData.customGroup}
                  onChange={handleCustomGroupChange}
                  placeholder="Enter group name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
            ></textarea>
          </div>
          
          <div className="flex justify-between pt-4">
            <button
              type="submit"
              disabled={submitting}
              className={`bg-[#FF5722] text-white px-6 py-2 rounded-md transition-colors duration-200 ${submitting ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#F4511E]'}`}
            >
              {submitting ? 'Adding...' : 'Add contact'}
            </button>
            <button
              type="button"
              className="bg-gray-100 text-gray-600 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors duration-200"
            >
              Add next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewContactPage; 